import { Avatar, Box, Paper, Typography, useTheme, Button } from '@mui/material';
import { format } from 'date-fns';
import { useState, useCallback } from 'react';

import { ChatMessage as ChatMessageType } from '@/types/chatWidget';

import DocumentViewModal from '@/components/document/DocumentViewModal';

interface ChatMessageProps {
  message: ChatMessageType;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const theme = useTheme();
  const isUser = message.sender === 'user';
  const [documentModalId, setDocumentModalId] = useState<string | null>(null);

  // Function to parse text and render document links and bold text
  const renderTextWithDocumentLinks = useCallback(
    (text: string) => {
      // Patterns to match different formats - updated to capture title as well
      const documentIdPattern =
        /\(external_document_id:\s*([a-f0-9-]{36})(?:\s*\|\s*title:\s*([^)]+))?\)/gi;
      const externalDocPattern =
        /External Document ID:\s*([a-f0-9-]{36})(?:\s*\|\s*title:\s*([^\n]+))?/gi;
      const boldPattern = /\*\*(.*?)\*\*/g;

      // Create array to store all matches with their types
      const allMatches: Array<{
        index: number;
        length: number;
        type: 'document' | 'bold';
        content: string;
        documentId?: string;
        title?: string;
      }> = [];

      // Find document ID matches (parentheses format)
      const documentMatches = Array.from(text.matchAll(documentIdPattern));
      documentMatches.forEach(match => {
        if (match.index !== undefined) {
          allMatches.push({
            index: match.index,
            length: match[0].length,
            type: 'document',
            content: match[0],
            documentId: match[1],
            title: match[2]?.trim(), // Extract title if present
          });
        }
      });

      // Find external document ID matches (External Document ID: format)
      const externalDocMatches = Array.from(text.matchAll(externalDocPattern));
      externalDocMatches.forEach(match => {
        if (match.index !== undefined) {
          allMatches.push({
            index: match.index,
            length: match[0].length,
            type: 'document',
            content: match[0],
            documentId: match[1],
            title: match[2]?.trim(), // Extract title if present
          });
        }
      });

      // Find bold text matches
      const boldMatches = Array.from(text.matchAll(boldPattern));
      boldMatches.forEach(match => {
        if (match.index !== undefined) {
          allMatches.push({
            index: match.index,
            length: match[0].length,
            type: 'bold',
            content: match[1], // Content between **
          });
        }
      });

      // Sort matches by index to process them in order
      allMatches.sort((a, b) => a.index - b.index);

      const parts = [];
      let lastIndex = 0;

      allMatches.forEach((match, index) => {
        // Check for overlapping matches (skip if this match overlaps with previous)
        if (index > 0) {
          const prevMatch = allMatches[index - 1];
          if (match.index < prevMatch.index + prevMatch.length) {
            return; // Skip overlapping match
          }
        }

        // Add text before the match
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }

        if (match.type === 'document' && match.documentId) {
          // Add the clickable document button
          const { documentId, title } = match;
          // Display title if available, otherwise show document ID
          const displayText = title || documentId;

          parts.push(
            <Button
              key={`doc-${documentId}-${match.index}`}
              onClick={() => setDocumentModalId(documentId)}
              sx={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                minWidth: 'auto',
                font: 'inherit',
                textTransform: 'none',
                '&:hover': {
                  color: theme.palette.primary.dark,
                  background: 'none',
                },
              }}
            >
              {displayText}
            </Button>
          );
        } else if (match.type === 'bold') {
          // Add bold text
          parts.push(<strong key={`bold-${match.index}`}>{match.content}</strong>);
        }

        lastIndex = match.index + match.length;
      });

      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      return parts.length > 1 ? parts : text;
    },
    [theme.palette.primary.main, theme.palette.primary.dark]
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: isUser ? '10px' : 2,
            maxWidth: '75%',
            borderRadius: isUser ? '6px' : 2,
            backgroundColor: isUser ? '#E8EAFD' : '#f5f5f5',
            color: isUser ? '#0D1C8C' : theme.palette.text.primary,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Typography
            variant="body2"
            component="div"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: 1.285,
              textAlign: isUser ? 'right' : 'left',
              whiteSpace: 'pre-wrap',
            }}
          >
            {renderTextWithDocumentLinks(message.content)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              textAlign: 'right',
              mt: 0.5,
              opacity: 0.8,
              fontSize: '0.7rem',
              display: 'none', // Hide timestamp for now based on Figma design
            }}
          >
            {format(new Date(message.timestamp), 'HH:mm')}
          </Typography>
        </Paper>
        {isUser && (
          <Avatar
            src="https://randomuser.me/api/portraits/women/68.jpg"
            sx={{
              width: 32,
              height: 32,
              ml: 1,
              alignSelf: 'flex-end',
              mb: 0.5,
              display: 'none', // Hide avatar for user messages based on Figma design
            }}
          />
        )}
      </Box>

      {/* Document Modal */}
      {documentModalId && (
        <DocumentViewModal id={documentModalId} onClose={() => setDocumentModalId(null)} />
      )}
    </>
  );
};

export default ChatMessage;
